// resources/js/Pages/Formula/ElementPalette.js
// resources/js/Pages/Formula/ElementPalette.js - UPDATED FOR FLAT + ARRAYS DESIGN
import React from 'react';
export default function ElementPalette({ onAddField, onDelete, onConvertToArray, selectedNode }) {
  if (!selectedNode) return null;
  const isRoot = selectedNode.key === 'Root' || selectedNode.type === 'root';
  const isField = selectedNode.type === 'field';
  const isArray = selectedNode.type === 'array' || selectedNode.type === 'list';
  const canDelete = !isRoot;
  // ✅ NEW: Get field type display info
  const getFieldTypeInfo = (valueType) => {
    switch (valueType) {
      case 'score':
        return { 
          label: 'Score', 
          color: 'success',
          icon: '💯',
          description: 'Number with color grade (1-10)'
        };
      case 'importance':
        return { 
          label: 'Importance', 
          color: 'warning',
          icon: '⚠️',
          description: 'Importance level with colored badge (1-5)'
        };
      case 'badge':
        return {
          label: 'Badge',
          color: 'secondary',
          icon: '🏷️',
          description: 'Custom colored badge using badgeMap'
        };
      case 'scene_number':
        return {
          label: 'Scene Link',
          color: 'warning',
          icon: '🎬',
          description: 'Single scene reference with link'
        };
      case 'scene_list':
        return { 
          label: 'Scene List', 
          color: 'warning',
          icon: '📋',
          description: 'Multiple scene references'
        };
      case 'questions_for_llm':
        return { 
          label: 'AI Questions', 
          color: 'info',
          icon: '🤖',
          description: 'Generates follow-up questions for analysis'
        };
      case 'string':
      default:
        return {
          label: 'Text',
          color: 'info',
          icon: '📝',
          description: 'General text or numeric content'
        };
    }
  };

  // ✅ NEW: Get style preset display info
  const getStyleInfo = (style) => {
    const styles = {
      warning: { label: 'Warning', icon: '⚠️', color: '#FF9800' },
      danger:  { label: 'Danger',  icon: '🚨', color: '#f44336' },
      info:    { label: 'Info',    icon: 'ℹ️', color: '#2196F3' },
      success: { label: 'Success', icon: '✅', color: '#4CAF50' },
      action:  { label: 'Action',  icon: '🎯', color: '#9C27B0' },
      neutral: { label: 'Neutral', icon: '📌', color: '#78909C' },
    };
    return styles[style] || null;
  };

  const getNodeIcon = () => {
    if (isRoot) return '🏠';
    if (isArray) {
      switch (selectedNode.display) {
        case 'cards': return '🃏';
        case 'table': return '📊';
        case 'tabs': return '📂';
        case 'ul': return '📋';
        default: return '📋';
      }
    }
    if (isField) {
      const fieldType = getFieldTypeInfo(selectedNode.valueType);
      return fieldType.icon;
    }
    return '📝';
  };

  const getNodeDescription = () => {
    if (isRoot) {
      return (
        <div className="small text-muted mb-3">
          <p className="mb-2">
            🏠 <strong>Root Container</strong><br/>
            Contains all your response fields in sequence.
          </p>
         
           <div className="alert alert-info p-2 small">
            <strong>Add fields one by one.</strong><br/>
            Each field becomes a property in the final JSON response.
          </div>
        </div>
      );
    }
    
    if (isField) {
      const fieldType = getFieldTypeInfo(selectedNode.valueType);
      return (
        <div className="small text-muted mb-3">
          <div className="d-flex align-items-center mb-2">
            <span className="me-2">{fieldType.icon}</span>
            <strong>{fieldType.label} Field</strong>
            <span className={`ms-2 badge bg-${fieldType.color}`} style={{ fontSize: '0.65em' }}>
              {fieldType.label}
            </span>
    
           </div>
          <p className="mb-2">{fieldType.description}</p>
          {selectedNode.instruction && (
            <p className="mb-1 fst-italic small">
              "{selectedNode.instruction.length > 80
                ? selectedNode.instruction.substring(0, 80) + '...'
                : selectedNode.instruction}"
            </p>
          )}
          {selectedNode.style && getStyleInfo(selectedNode.style) && (
            <div className="mt-2 p-2 rounded" style={{
              backgroundColor: getStyleInfo(selectedNode.style).color + '18',
              borderLeft: `3px solid ${getStyleInfo(selectedNode.style).color}`
            }}>
              <small>
                <strong>{getStyleInfo(selectedNode.style).icon} Style:</strong> {getStyleInfo(selectedNode.style).label}
              </small>
            </div>
          )}
         </div>
      );
    }
    
    if (isArray) {
      const display = selectedNode.display ||
      'ul';
      const instructionStatus = selectedNode.instruction ? '✅' : '⚠️';
      
      return (
        <div className="small text-muted mb-3">
          <div className="d-flex align-items-center mb-2">
            <span className="me-2">{getNodeIcon()}</span>
            <strong>Array ({display})</strong>
            <span className="ms-2 badge bg-primary" style={{ fontSize: '0.65em' }}>
              {display}
       
           </span>
          </div>
          
          <div className={`p-2 rounded mb-2 ${selectedNode.instruction ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'}`}>
            <div className="d-flex align-items-center">
              <span className="me-2">{instructionStatus}</span>
              <strong>Array Instruction:</strong>
           
           </div>
            {selectedNode.instruction ? (
              <small className="text-success">
                "{selectedNode.instruction.length > 60 
                  ? selectedNode.instruction.substring(0, 60) + '...' 
                  : selectedNode.instruction}"
         
           </small>
            ) : (
              <small className="text-warning">
                Required: What should each {display === 'cards' ?
                'card' : display === 'table' ? 'row' : display === 'tabs' ? 'tab' : 'item'} represent?
              </small>
            )}
          </div>
          
          <p className="mb-1">
            <strong>This creates multiple items</strong> - each with the fields you define below.
          </p>
        </div>
      );
    }
    
    return null;
  };
  return (
    <div className="card mb-3 element-palette">
      <div className="card-header">
        <div className="d-flex align-items-center">
          <span className="me-2">{getNodeIcon()}</span>
          <div>
            <strong>{selectedNode.key}</strong>
            <span className="ms-2 badge bg-secondary">{isArray ? 'array' : selectedNode.type}</span>
            {/* ✅ Show field type for fields */}

             {isField && (
              <span className={`ms-1 badge bg-${getFieldTypeInfo(selectedNode.valueType).color}`}
                    style={{ fontSize: '0.65em' }}>
                {getFieldTypeInfo(selectedNode.valueType).label}
              </span>
            )}
            {/* ✅ Show style preset badge */}
            {isField && selectedNode.style && getStyleInfo(selectedNode.style) && (
              <span className="ms-1 badge"
                    style={{ fontSize: '0.65em', backgroundColor: getStyleInfo(selectedNode.style).color, color: '#fff' }}>
                {getStyleInfo(selectedNode.style).icon} {getStyleInfo(selectedNode.style).label}
              </span>
            )}
       
             {/* ✅ Show display type for arrays */}
            {isArray && selectedNode.display && (
              <span className="ms-1 badge bg-primary" style={{ fontSize: '0.65em' }}>
                {selectedNode.display}
              </span>
            )}
         
           </div>
        </div>
      </div>
      <div className="card-body">
        {getNodeDescription()}
        
        {/* ✅ ROOT or ARRAY: Show Add Element Button */}
        {(isRoot || isArray) && (
          <div className="d-grid gap-2">
            <button 
 
               className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center"
              onClick={onAddField}
            >
              <span className="me-2">📝</span>
              Add Field
            </button>
          </div>
        )}
        
        {/* ✅ DELETE (for non-root) */}
        {canDelete && (
          <div className="mt-3 pt-3 border-top">
            <button 
 
               className="btn btn-outline-danger btn-sm w-100 d-flex align-items-center justify-content-center"
              onClick={onDelete}
            >
              <span className="me-2">🗑️</span>
              Delete This {isArray ? 'Array' : 'Field'}
            </button>
          </div>
        )}
  
       
        {!canDelete && (
          <div className="mt-3 pt-3 border-top">
            <small className="text-muted">
              💡 Root container cannot be deleted
            </small>
          </div>
        )}
      </div>
   
     </div>
  );
}